import React, { memo, useCallback } from "react";

import { useTranslation } from "react-i18next";

import "../../css/admin/utils/module-image-upload.scss";
import Block from "../block/Block";
import SelectBox from "../selectBox/SelectBox";
import { SELECT_BOX_TYPES } from "../selectBox/types/selectBoxTypes";
import SmartlistCastingsBlock from "../smartlist/SmartlistCastingsBlock";
import SmartlistTranslations from "../smartlist/SmartlistTranslations";
import SwitchWithLabel from "../utils/SwitchWithLabel";

import type { JSX } from "react";

import type {
  SmartlistCasting,
  SmartlistItem,
  SmartlistSeason,
  SmartlistSeasonUpdated,
} from "../../api/SmartlistAPI.types";
import type { SelectBoxDisplayItem } from "../selectBox/selectBox.types";
import type { SmartlistTranslationsHandle } from "../smartlist/SmartlistTranslations";

interface Tab {
  key: string;
  icon: string;
  index: number;
}

interface SeasonFormProps {
  baseUrl: string;
  onChangeLibraryItems: (
    seasonUuid: string,
    items: (SelectBoxDisplayItem | SmartlistItem)[],
  ) => void;
  onRegisterTranslations: (
    seasonUuid: string,
    handle: SmartlistTranslationsHandle | null,
  ) => void;
  season: SmartlistSeason | SmartlistSeasonUpdated;
  tabs: Tab[];
  onChangeEpisodesOrder: (seasonUuid: string, value: boolean) => void;
  onChangeCastings: (seasonUuid: string, castings: SmartlistCasting[]) => void;
}

function SeasonForm({
  baseUrl,
  onChangeLibraryItems,
  onRegisterTranslations,
  season,
  tabs,
  onChangeEpisodesOrder,
  onChangeCastings,
}: SeasonFormProps): JSX.Element {
  const { t } = useTranslation("editor-smartlist");

  const registerTranslations = useCallback(
    (handle: SmartlistTranslationsHandle | null): void => {
      onRegisterTranslations(season.uuid, handle);
    },
    [onRegisterTranslations, season.uuid],
  );

  const handleChangeItems = useCallback(
    (items: (SelectBoxDisplayItem | SmartlistItem)[]): void => {
      onChangeLibraryItems(season.uuid, items);
    },
    [onChangeLibraryItems, season.uuid],
  );

  return (
    <div className="flex flex-col gap-[0.9375rem]">
      <SmartlistTranslations
        ref={registerTranslations}
        baseUrl={baseUrl}
        smartlist={season}
      />
      <Block title={t("elements")}>
        <SelectBox
          hasRank
          ignoreSmartlistsAudios
          items={season.smartlist_items}
          label={t("editor-series:elementsSeason")}
          onChangeItems={handleChangeItems}
          selectBoxType={SELECT_BOX_TYPES.SEASONS}
          tabs={tabs}
        />
      </Block>
      <SmartlistCastingsBlock
        onChange={(castings): void => onChangeCastings(season.uuid, castings)}
        value={season.castings ?? []}
      />
      <Block title={t("displayOption")}>
        <SwitchWithLabel
          isChecked={season.reversed}
          label={t("editor-series:reverse_episodes")}
          name={`reverse-episodes-${season.uuid}`}
          onChange={(value): void => onChangeEpisodesOrder(season.uuid, value)}
        />
      </Block>
    </div>
  );
}

// Every season stays mounted (hidden tabs): only the edited one must re-render.
export default memo(SeasonForm);
